// Dashboard personalizado para usuários autenticados, Fase 1.
//
// Visitantes anônimos continuam vendo a Home pastoral em <Home/>.
// Esta tela é exibida em "/" APENAS quando isAuthenticated && !isAdmin
// (admin sempre vai pro painel /admin via redirect próprio).
//
// O conteúdo do dia (verso/sermão/oração) vem do /api/daily (kit já integrado).

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Mic,
  HandHeart,
  Activity,
  CalendarCheck,
  Sun,
  Moon,
  Sunset,
  ArrowRight,
  Sparkles,
  Quote,
} from "lucide-react";
import { useAuth } from "../lib/authContext";
import useDaily from "../lib/useDaily";
import {
  getReadingProgress,
  getReadingHistory,
  getActivePlan,
  summarizePlanProgress,
} from "../lib/readingProgress";
import { journeyApi } from "../lib/journeyApi";
import DashboardTour from "../components/DashboardTour";
import MyImpactCard from "../features/share/MyImpactCard";
import StreakVisual from "../components/StreakVisual";
import PrayerWeekCard from "../components/PrayerWeekCard";
import { fetchSermons } from "../lib/api";
import { isTodayCompleted, syncTodayCompletionFromServer } from "../lib/dailyJourney";
import { WelcomeMessageCard } from "../components/WelcomeMessageCard";

function pickGreeting(hour) {
  if (hour < 12) return { txt: "Bom dia", Icon: Sun };
  if (hour < 18) return { txt: "Boa tarde", Icon: Sunset };
  return { txt: "Boa noite", Icon: Moon };
}

function profileLabel(profile) {
  return (
    {
      new_convert: "Novo convertido",
      member: "Membro",
      leader: "Líder",
      pastor: "Pastor",
      seminarian: "Seminarista",
    }[profile] || "Caminhante"
  );
}

function dateLine() {
  const d = new Date();
  return d.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function PlanCard({ activePlan, summary }) {
  if (!activePlan?.slug) {
    return (
      <Link
        to="/planos"
        data-testid="dashboard-card-plans"
        className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
      >
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
          <CalendarCheck className="w-4 h-4 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
            Plano de hoje
          </p>
          <p className="font-display text-lg text-navy mt-0.5">
            Comece um plano de leitura
          </p>
          <p className="text-xs text-navy/55 mt-1">
            Jornadas de 5 a 30 dias para fortalecer sua caminhada.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
      </Link>
    );
  }
  const day = summary?.current_day || 1;
  return (
    <Link
      to={summary?.last_href || `/planos/${activePlan.slug}/dia/${day}`}
      data-testid="dashboard-card-plans"
      className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
        <CalendarCheck className="w-4 h-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
          Plano de hoje
        </p>
        <p className="font-display text-lg text-navy mt-0.5 truncate">
          {activePlan.plan_title || activePlan.slug}
        </p>
        <p className="text-xs text-navy/55 mt-1">
          Continuar, Dia {day}
          {summary?.total_days
            ? ` de ${summary.total_days} (${summary.percent}%)`
            : ""}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
    </Link>
  );
}

function ContinueReadingCard({ progress, history }) {
  const latest = progress || history?.[0] || null;
  if (!latest?.href) {
    return (
      <Link
        to="/biblia"
        data-testid="dashboard-card-continue"
        className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
      >
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
          <BookOpen className="w-4 h-4 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
            Continue sua leitura
          </p>
          <p className="font-display text-lg text-navy mt-0.5">
            Abrir a Bíblia
          </p>
          <p className="text-xs text-navy/55 mt-1">
            Você ainda não leu hoje, comece por Salmos 23.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
      </Link>
    );
  }
  return (
    <Link
      to={latest.href}
      data-testid="dashboard-card-continue"
      className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
        <BookOpen className="w-4 h-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
          Continue sua leitura
        </p>
        <p className="font-display text-lg text-navy mt-0.5 truncate">
          {latest.title || "Última leitura"}
        </p>
        <p className="text-xs text-navy/55 mt-1 truncate">
          {latest.subtitle || "Toque para retomar."}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
    </Link>
  );
}

function SermonOfDayCard({ daily }) {
  const s = daily?.sermon;
  const hasSermon = s?.title;
  return (
    <Link
      to={hasSermon && s?.slug ? `/sermoes/${s.slug}` : "/sermoes"}
      data-testid="dashboard-card-sermon"
      className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
        <Mic className="w-4 h-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
          Sermão do dia
        </p>
        <p className="font-display text-lg text-navy mt-0.5 truncate">
          {hasSermon ? s.title : "Explorar sermões"}
        </p>
        <p className="text-xs text-navy/55 mt-1">
          {hasSermon ? s.category || "Pastor Carlos Medeiros" : "Acervo completo"}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
    </Link>
  );
}

function PrayerOfDayCard({ daily }) {
  const text = daily?.prayer || "";
  const preview =
    text.length > 130
      ? `${text.slice(0, 127).trim()}…`
      : text || "Envie seu pedido de oração.";
  return (
    <Link
      to="/oracao"
      data-testid="dashboard-card-prayer"
      className="rounded-2xl bg-[hsl(var(--parchment))] border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
    >
      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
        <HandHeart className="w-4 h-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold">
          Oração de hoje
        </p>
        <p className="text-navy/85 italic text-sm mt-1.5 leading-relaxed">
          “{preview}”
        </p>
      </div>
    </Link>
  );
}

function JourneyCard({ streak }) {
  // Parte 5 — jornada recém iniciada: convite acolhedor em vez de pontos vazios.
  const isNew =
    !streak ||
    ((streak.current || 0) === 0 &&
      (streak.longest || 0) === 0 &&
      !(streak.week || []).some((d) => d?.active));
  if (isNew) {
    return (
      <Link
        to="/jornada"
        data-testid="dashboard-card-journey"
        className="rounded-2xl bg-white border border-[#B8962E]/30 hover:border-[#B8962E]/60 hover:shadow-sm transition-all p-5 sm:p-6 block group"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
          Minha jornada
        </p>
        <p
          className="font-display text-xl sm:text-2xl text-navy mt-1.5"
          data-testid="dashboard-journey-invite"
        >
          Sua jornada começa hoje
        </p>
        <p className="text-sm text-navy/65 mt-2 leading-relaxed max-w-md">
          Registre seu primeiro passo espiritual e acompanhe sua caminhada com
          Deus, um dia de cada vez.
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold group-hover:gap-3 transition-all">
          Começar agora
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    );
  }
  return (
    <Link
      to="/jornada"
      data-testid="dashboard-card-journey"
      className="rounded-2xl bg-white border border-[#B8962E]/30 hover:border-[#B8962E]/60 hover:shadow-sm transition-all p-5 block group"
    >
      <StreakVisual
        current={streak?.current || 0}
        longest={streak?.longest || 0}
        week={streak?.week || []}
        variant="light"
        eyebrow="Minha jornada"
        testid="dashboard-streak-visual"
      />
      <div className="mt-4 pt-4 border-t border-navy/8 flex items-center justify-between text-xs text-navy/60">
        <span>Espiritual · Família · Saúde · Propósito</span>
        <span className="inline-flex items-center gap-1 text-gold uppercase tracking-widest text-[10px] group-hover:gap-2 transition-all">
          Ver jornada
          <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

export default function DashboardLogado() {
  const { user } = useAuth();
  const { daily } = useDaily();
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [journeyStreak, setJourneyStreak] = useState(null);
  const [recentSermons, setRecentSermons] = useState([]);

  useEffect(() => {
    try {
      setProgress(getReadingProgress());
    } catch {
      /* ignore */
    }
    try {
      setHistory(getReadingHistory());
    } catch {
      /* ignore */
    }
    try {
      const p = getActivePlan();
      setActivePlan(p);
      if (p?.slug) {
        setSummary(summarizePlanProgress(p.slug, p.total_days || 0));
      }
    } catch {
      /* ignore */
    }
    journeyApi
      .streak()
      .then(setJourneyStreak)
      .catch(() => {});
    // B4 — paridade com Home: carrega sermões recentes (3 primeiros).
    fetchSermons({ limit: 6 })
      .then((data) => {
        const arr = Array.isArray(data) ? data : data?.items || [];
        setRecentSermons(arr.slice(0, 3));
      })
      .catch(() => setRecentSermons([]));
  }, []);

  const greeting = useMemo(() => pickGreeting(new Date().getHours()), []);
  const GreetIcon = greeting.Icon;
  const firstName = (user?.name || "").split(" ")[0] || "Caminhante";
  const profile = profileLabel(user?.spiritual_profile);
  const verseRef = daily?.central_verse?.reference;
  const verseTxt = daily?.central_verse?.text;
  const [devotionalDone, setDevotionalDone] = useState(isTodayCompleted());
  useEffect(() => {
    let alive = true;
    syncTodayCompletionFromServer().then((v) => alive && setDevotionalDone(v));
    return () => { alive = false; };
  }, []);

  return (
    <div
      className="max-w-5xl mx-auto section-padding py-8"
      data-testid="dashboard-logged"
    >
      {/* Header / greeting */}
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-navy/55">
        <GreetIcon className="w-3.5 h-3.5 text-gold" />
        <span className="capitalize">{dateLine()}</span>
      </div>
      <h1
        className="font-display text-4xl sm:text-5xl text-navy mt-2"
        data-testid="dashboard-greeting"
      >
        {greeting.txt}, {firstName}!
      </h1>
      <p className="text-navy/60 text-sm mt-2 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 text-[10px] uppercase tracking-widest text-navy">
          <Sparkles className="w-3 h-3 text-gold" />
          {profile}
        </span>
        <span className="text-navy/55">· Sua jornada continua hoje.</span>
      </p>

      {/* Hero verse strip */}
      {verseTxt && (
        <div
          data-testid="dashboard-hero-verse"
          className="mt-6 rounded-2xl bg-navy text-white/95 px-6 py-5 flex gap-4 items-start"
        >
          <Quote className="w-5 h-5 text-gold shrink-0 mt-1" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Versículo do dia
            </p>
            <p className="font-serif text-base sm:text-lg leading-relaxed mt-1.5">
              “{verseTxt}”
            </p>
            {verseRef && (
              <p className="text-[11px] uppercase tracking-widest text-white/55 mt-2">
                {verseRef}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parte 4, mensagem de acolhimento, imediatamente após a saudação */}
      <WelcomeMessageCard />

      {/* 1. Devocional do dia, com status Concluído / Pendente */}
      {daily?.devotional?.title && (
        <section className="mt-8" data-testid="dashboard-devotional-section">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Devocional de hoje</span>
            <span
              data-testid="dashboard-devotional-status"
              className={`text-[10px] uppercase tracking-widest px-2.5 py-0.5 rounded-full ${
                devotionalDone
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-gold/15 text-navy/70"
              }`}
            >
              {devotionalDone ? "Concluído" : "Pendente"}
            </span>
            <span className="flex-1 h-px bg-border" />
          </div>
          <Link
            to={daily.devotional.slug ? `/devocional/${daily.devotional.slug}` : "/devocional"}
            data-testid="dashboard-devotional-card"
            className="mt-4 block rounded-2xl bg-white border border-border p-5 sm:p-6 hover:border-gold/60 hover:shadow-sm transition-all group"
          >
            <p className="font-display text-2xl sm:text-3xl text-navy leading-snug group-hover:text-gold transition-colors">
              {daily.devotional.title}
            </p>
            {daily.pastoral_blocks?.abertura && (
              <p className="mt-3 text-navy/70 text-sm leading-relaxed line-clamp-3">
                {daily.pastoral_blocks.abertura}
              </p>
            )}
            <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold group-hover:gap-3 transition-all">
              Ler devocional completo <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        </section>
      )}

      {/* 2. Semana temática, somente o resumo do dia atual (ponto único da tela) */}
      <section className="mt-8" data-testid="dashboard-prayer-week-section">
        <PrayerWeekCard todayOnly />
      </section>

      {/* 3. Minha jornada */}
      <section className="mt-8" data-testid="dashboard-journey-section">
        <JourneyCard streak={journeyStreak} />
      </section>

      {/* 4-6. Plano de leitura, Sermão do dia, Auxiliar Bíblico + demais cards */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dashboard-cards">
        <PlanCard activePlan={activePlan} summary={summary} />
        <SermonOfDayCard daily={daily} />
        <Link
          to="/assistente"
          data-testid="dashboard-card-assistant"
          className="rounded-2xl bg-white border border-border p-5 hover:border-gold/70 hover:shadow-sm transition-all flex items-start gap-4 group"
        >
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
              Auxiliar Bíblico
            </p>
            <p className="font-display text-lg text-navy mt-0.5">
              Converse com a IA
            </p>
            <p className="text-xs text-navy/55 mt-1">
              Tire dúvidas e receba reflexões personalizadas.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors mt-1.5" />
        </Link>
        <ContinueReadingCard progress={progress} history={history} />
        <PrayerOfDayCard daily={daily} />
      </div>

      {/* Meu Impacto, só renderiza se logado e com pelo menos 1 share */}
      <div className="mt-6" data-testid="dashboard-impact-slot">
        <MyImpactCard />
      </div>

      {/* B4 — Sermões recentes (paridade com Home) */}
      {recentSermons.length > 0 && (
        <section className="mt-8" data-testid="dashboard-sermons-section">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Sermões recentes</span>
              <span className="flex-1 h-px bg-border" />
            </div>
            <Link
              to="/sermoes"
              data-testid="dashboard-sermons-all"
              className="text-[10px] uppercase tracking-widest text-navy/60 hover:text-gold whitespace-nowrap"
            >
              Ver todos
            </Link>
          </div>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="dashboard-sermons-list">
            {recentSermons.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/sermoes/${s.id}`}
                  data-testid={`dashboard-sermon-${s.id}`}
                  className="block rounded-2xl bg-white border border-border p-4 hover:border-gold/60 hover:shadow-sm transition-all h-full group"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                    {s.category || "Sermão"}
                  </p>
                  <p className="mt-1.5 font-display text-navy leading-snug group-hover:text-gold transition-colors line-clamp-3">
                    {s.title}
                  </p>
                  {s.base_text && (
                    <p className="mt-2 text-[11px] text-gold tracking-wide truncate">
                      {s.base_text}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Favoritos, Diário e Histórico (conteúdos pessoais) */}
      <div className="mt-8 grid grid-cols-3 gap-3" data-testid="dashboard-personal-links">
        {[
          { to: "/favoritos", label: "Favoritos", testid: "dashboard-link-favoritos" },
          { to: "/diario", label: "Diário", testid: "dashboard-link-diario" },
          { to: "/historico", label: "Histórico", testid: "dashboard-link-historico" },
        ].map((l) => (
          <Link
            key={l.to}
            to={l.to}
            data-testid={l.testid}
            className="rounded-2xl bg-white border border-border px-3 py-4 text-center text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold hover:border-gold/60 transition-colors"
          >
            {l.label}
          </Link>
        ))}
      </div>

      <p className="text-center text-[11px] text-navy/45 mt-10">
        Sua caminhada importa. Que o Senhor abençoe seu dia.
      </p>

      {/* 3-tap onboarding tour, aparece somente 1x via localStorage */}
      <DashboardTour />
    </div>
  );
}
